import axios from "axios";
import { tokenStorage } from "../utils/tokenStorage";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];

const publicAuthPaths = [
  "/api/v1/power/auth/login",
  "/api/v1/power/auth/setup-mfa",
  "/api/v1/power/auth/verify-mfa",
  "/api/v1/power/account-setup/validate-token",
  "/api/v1/power/account-setup/start",
  "/api/v1/power/account-setup/verify-mfa",
];

const shouldSkipRefresh = (url = "") => {
  return publicAuthPaths.some((path) => url.includes(path));
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });

  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    const accessToken = tokenStorage.getAccessToken();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    const normalizedError = {
      status: error?.response?.status,
      message:
        error?.response?.data?.message ||
        error?.message ||
        "Er is iets fout gegaan.",
      data: error?.response?.data || null,
    };

    if (!error.response || !originalRequest) {
      return Promise.reject(normalizedError);
    }

    if (error.response.status !== 401) {
      return Promise.reject(normalizedError);
    }

    if (shouldSkipRefresh(originalRequest.url)) {
      return Promise.reject(normalizedError);
    }

    if (originalRequest._retry) {
      tokenStorage.clearAuth();
      window.location.href = "/login";
      return Promise.reject(normalizedError);
    }

    const refreshToken = tokenStorage.getRefreshToken();

    if (!refreshToken) {
      tokenStorage.clearAuth();
      window.location.href = "/login";
      return Promise.reject(normalizedError);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/power/auth/refresh`,
        { refreshToken },
      );

      const data = response.data?.data || {};

      const newAccessToken = data.access_token || data.accessToken;
      const newRefreshToken = data.refresh_token || data.refreshToken;
      const user = data.user || tokenStorage.getUser();

      if (!newAccessToken || !newRefreshToken) {
        throw new Error("Ongeldige refresh response.");
      }

      tokenStorage.saveAuth({
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        user,
      });

      api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      processQueue(null, newAccessToken);

      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);

      tokenStorage.clearAuth();
      window.location.href = "/login";

      return Promise.reject({
        status: refreshError?.response?.status,
        message: refreshError?.response?.data?.message || "Sessie verlopen.",
        data: refreshError?.response?.data || null,
      });
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
